import { queryOptions, useMutation, useQuery } from '@tanstack/react-query'
import api from '../api/client'
import type { ActiveSession, DifficultWordsResponse, LearningSessionStatus, ProgressSummary, RecentDeck, SmartQueueSessionResponse, TopicTrainSessionResponse, VocabularyMapResponse } from '../types'

/**
 * The query definitions below are shared by the hooks and by the neighbour
 * prefetch (`utils/neighbourPrefetch.ts`), so both ask for exactly the same
 * key with the same fetcher and staleness. `abortable` is for the prefetch
 * only: its request can then be cancelled when the learner moves on before it
 * returns. The hooks leave it off, so their loading is unchanged.
 */

/** Request options: the query's own abort signal, only when asked for. */
const aborting = (abortable: boolean, signal: AbortSignal) => (abortable ? { signal } : undefined)

// ── Progress summary ────────────────────────────────────────────────────────

export const progressSummaryQuery = (abortable = false) => queryOptions<ProgressSummary>({
  queryKey: ['progress-summary'],
  queryFn: (ctx) => api.get('/api/v1/progress/summary/', aborting(abortable, ctx.signal)).then((r) => r.data),
  staleTime: 30_000, // 30 s — fresh enough for a home screen
})

export function useProgressSummary() {
  return useQuery(progressSummaryQuery())
}

// ── Vocabulary map ──────────────────────────────────────────────────────────

export const vocabularyMapQuery = (abortable = false) => queryOptions<VocabularyMapResponse>({
  queryKey: ['vocabulary-map'],
  queryFn: (ctx) => api.get('/api/v1/progress/vocabulary-map/', aborting(abortable, ctx.signal)).then((r) => r.data),
  staleTime: 5 * 60 * 1000, // 5 min — matches the backend cache TTL
})

export function useVocabularyMap() {
  return useQuery(vocabularyMapQuery())
}

// ── Difficult words ──────────────────────────────────────────────────────────

export const difficultWordsQuery = (abortable = false) => queryOptions<DifficultWordsResponse>({
  queryKey: ['difficult-words'],
  queryFn: (ctx) => api.get('/api/v1/progress/difficult-words/', aborting(abortable, ctx.signal)).then((r) => r.data),
  staleTime: 5 * 60 * 1000, // 5 min — changes only after study sessions
})

export function useDifficultWords() {
  return useQuery(difficultWordsQuery())
}

// ── Recent focus-studied decks (Home "jump back into a deck" cards) ──────────

export const recentDecksQuery = (abortable = false) => queryOptions<RecentDeck[]>({
  queryKey: ['recent-decks'],
  queryFn: (ctx) => api.get('/api/v1/learning/recent-decks/', aborting(abortable, ctx.signal)).then((r) => r.data),
  staleTime: 30_000, // matches the other Home queries; refetches on remount
})

export function useRecentDecks() {
  return useQuery(recentDecksQuery())
}

// ── Active learning session ─────────────────────────────────────────────────

// Source-blind winner — kept for completeness; not consumed by Home after ADR-045.
export function useActiveSession() {
  return useQuery<ActiveSession | null>({
    queryKey: ['active-session'],
    queryFn: () =>
      api.get('/api/v1/learning/sessions/active').then((r) => r.data ?? null),
    staleTime: 30_000,
  })
}

// Returns the most-recently-paused *smart-queue* session (source_type="smart_queue").
// Home's primary Continue CTA is bound to the daily stream exclusively (ADR-045).
// Uses its own query key so that invalidation and the source-blind hook don't alias.
export const smartQueueSessionQuery = (abortable = false) => queryOptions<ActiveSession | null>({
  queryKey: ['active-session-sq'],
  queryFn: (ctx) =>
    api.get('/api/v1/learning/sessions/active?source_type=smart_queue', aborting(abortable, ctx.signal)).then((r) => r.data ?? null),
  staleTime: 30_000,
})

export function useSmartQueueSession() {
  return useQuery(smartQueueSessionQuery())
}

// ── Single learning session status (reviewed / total for Continue label) ────

export const sessionStatusQuery = (sessionId: string | undefined, abortable = false) => queryOptions<LearningSessionStatus>({
  queryKey: ['learning-session-status', sessionId],
  queryFn: (ctx) =>
    api.get(`/api/v1/learning/sessions/${sessionId}`, aborting(abortable, ctx.signal)).then((r) => r.data),
  enabled: !!sessionId,
  staleTime: 30_000,
})

export function useSessionStatus(sessionId: string | undefined) {
  return useQuery(sessionStatusQuery(sessionId))
}

// ── Smart queue session ─────────────────────────────────────────────────────

export function useStartSmartQueue() {
  return useMutation<SmartQueueSessionResponse, Error>({
    mutationFn: () =>
      api.post('/api/v1/smart-queue/session/').then((r) => r.data),
  })
}

// ── Topic-focused training session ─────────────────────────────────────────

export function useStartTopicTrain() {
  return useMutation<TopicTrainSessionResponse, Error, string>({
    mutationFn: (topic: string) =>
      api.post('/api/v1/smart-queue/topic-session/', { topic }).then((r) => r.data),
  })
}
